<template>
  <div class="sub-editor">
    <div v-if="loading" class="sub-editor__loading">
      <v-progress-circular color="primary" indeterminate size="28" />
    </div>

    <template v-else>
      <!-- Aktive Vertretungen dieses Kontos -->
      <div v-if="activeEntries.length > 0" class="sub-banner sub-banner--away">
        <CalendarOff :size="16" />
        <span>
          {{ isSelf ? $gt('Sie werden aktuell vertreten von') : $gt('Wird aktuell vertreten von') }}
          <strong>{{ activeEntries.map((e) => scopedSubstituteName(e)).join(', ') }}</strong>.
        </span>
      </div>

      <!-- Wen dieses Konto selbst vertritt -->
      <div v-if="activeRepresenting.length > 0" class="sub-banner sub-banner--represent">
        <UserRoundCheck :size="16" />
        <span>
          {{ isSelf ? $gt('Sie vertreten aktuell') : $gt('Vertritt aktuell') }}
          <strong>{{ activeRepresenting.map((e) => representingLabel(e)).join(', ') }}</strong>.
        </span>
      </div>

      <!-- Regel-Liste -->
      <div class="sub-list">
        <div v-if="drafts.length === 0" class="sub-empty">
          {{ $gt('Keine Vertretung hinterlegt.') }}
        </div>
        <div v-for="(d, i) in drafts" :key="d.key" class="sub-row" :class="{ 'sub-row--active': isDraftActive(d) }">
          <div class="sub-row__main">
            <span class="sub-row__name">{{ candidateName(d.idSubstitute) }}</span>
            <span class="sub-row__period">
              {{ formatDate(d.validFrom) }}
              &ndash;
              {{ d.validUntil ? formatDate(d.validUntil) : $gt('bis auf Weiteres') }}
            </span>
            <!-- Fallauswahl (Kundenfeedback 13.09.26, I) -->
            <span v-if="scopeLabel(d)" class="sub-row__scope" :title="$gt('Fallauswahl dieser Vertretung')">
              <ListFilter :size="12" />
              {{ scopeLabel(d) }}
            </span>
            <span v-if="d.note" class="sub-row__note">{{ d.note }}</span>
          </div>
          <span v-if="isDraftActive(d)" class="sub-chip">{{ $gt('aktiv') }}</span>
          <button class="sub-row__remove" :title="$gt('Vertretung entfernen')" type="button" @click="removeDraft(i)">
            <Trash2 :size="15" />
          </button>
        </div>
      </div>

      <!-- Hinzufügen -->
      <div class="sub-add">
        <div class="sub-field sub-field--grow">
          <label class="sub-label">{{ $gt('Vertretung') }}</label>
          <select v-model="form.idSubstitute" class="sub-input">
            <option disabled :value="0">{{ $gt('– Person auswählen –') }}</option>
            <option v-for="c in availableCandidates" :key="c.id" :value="c.id">
              {{ c.name }}{{ c.userRole ? ` (${c.userRole})` : '' }}
            </option>
          </select>
        </div>
        <div class="sub-field">
          <label class="sub-label">{{ $gt('Von') }}</label>
          <input v-model="form.validFrom" class="sub-input" type="date">
        </div>
        <div class="sub-field">
          <label class="sub-label">{{ $gt('Bis (optional)') }}</label>
          <input v-model="form.validUntil" class="sub-input" :min="form.validFrom || undefined" type="date">
        </div>
        <div class="sub-field sub-field--grow">
          <label class="sub-label">{{ $gt('Notiz (optional)') }}</label>
          <input
            v-model="form.note"
            class="sub-input"
            maxlength="255"
            :placeholder="$gt('z. B. Urlaub, Krankheit')"
            type="text"
          >
        </div>
        <!-- Fallauswahl (Kundenfeedback Kanzlei 13.09.26, I): Teilen sich
             mehrere Vertretungen die Akten auf, übernimmt z. B. Frau
             Schieschke die ungeraden und Frau Solinas die geraden
             Fallnummern; eigene Nummernbereiche sind ebenfalls möglich. -->
        <div class="sub-field sub-field--scope">
          <label class="sub-label">{{ $gt('Fallauswahl') }}</label>
          <select v-model="form.caseScope" class="sub-input">
            <option v-for="o in scopeOptions" :key="o.value" :value="o.value">{{ $gt(o.label) }}</option>
          </select>
        </div>
        <div v-if="form.caseScope === 'custom'" class="sub-field sub-field--grow">
          <label class="sub-label">{{ $gt('Nummernbereiche') }}</label>
          <input
            v-model="form.caseScopeRule"
            class="sub-input"
            maxlength="255"
            :placeholder="$gt('z. B. 1-50, 77, 120-')"
            type="text"
          >
        </div>
        <button class="sub-btn sub-btn--secondary" :disabled="!canAdd" type="button" @click="addDraft">
          <Plus :size="15" />
          {{ $gt('Hinzufügen') }}
        </button>
        <p class="sub-scope-hint">
          <ListFilter :size="13" />
          {{ $gt('Fallauswahl: Die Vertretung gilt für alle Akten oder nur für ungerade, gerade bzw. selbst gewählte Fallnummern – z. B. wenn sich zwei Kolleginnen die Akten aufteilen. Grundlage ist die laufende Nummer des Aktenzeichens; ohne Aktenzeichen die KARS-Fall-ID.') }}
        </p>
      </div>

      <!-- Abwesenheitsnotiz mit automatischer Antwort (Kundenfeedback
           Kanzlei 18.10.2026, V). Greift nur während einer laufenden
           Vertretungsregel; ohne Zeitraum für Externe. -->
      <AbsenceNoticeSettings
        v-model="notice"
        :active="drafts.some(d => isDraftActive(d))"
        :default-message="defaultNoticeText"
        :has-period="drafts.length > 0"
        :owner-name="ownerName"
      />

      <p v-if="formError" class="sub-error">{{ formError }}</p>

      <div class="sub-actions">
        <span v-if="dirty" class="sub-dirty">{{ $gt('Ungespeicherte Änderungen') }}</span>
        <button class="sub-btn" :disabled="!dirty || saving" type="button" @click="save">
          <v-progress-circular v-if="saving" indeterminate size="14" width="2" />
          {{ $gt('Vertretung speichern') }}
        </button>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
  // Gemeinsamer Editor für „Mein Profil“ (eigene Vertretung) und den
  // Admin-Dialog in der Benutzerverwaltung (Vertretung eines anderen Nutzers).
  // Regeln werden lokal gesammelt und mit „Vertretung speichern“ komplett
  // gespeichert (PUT ersetzt die Liste).
  import { CalendarOff, ListFilter, Plus, Trash2, UserRoundCheck } from 'lucide-vue-next'
  import AbsenceNoticeSettings from './AbsenceNoticeSettings.vue'
  import { computed, onMounted, reactive, ref, watch } from 'vue'
  import { gt } from '@/i18n/gt'
  import {
    type AbsenceNoticeSettings as AbsenceNotice,
    describeSubstitutionScope,
    formatSubstitutionDate,
    getSubstitutionCandidates,
    getSubstitutions,
    isValidSubstitutionScopeRule,
    saveSubstitutions,
    SUBSTITUTION_CASE_SCOPES,
    type SubstitutionCandidate,
    type SubstitutionCaseScope,
    type SubstitutionDraft,
    type SubstitutionEntry,
  } from '@/services/substitutionApi'
  import { useSnackbarStore } from '@/stores/snackbarStore'

  interface DraftRow extends SubstitutionDraft {
    key: number
  }

  const props = defineProps<{
    /** null = eigenes Konto (Endpunkte /users/me/…), sonst Nutzer-ID (Admin). */
    userId?: number | null
  }>()

  const emit = defineEmits<{
    (e: 'saved'): void
    (e: 'dirty', value: boolean): void
  }>()

  const isSelf = computed(() => !props.userId)

  const loading = ref(true)
  const saving = ref(false)
  const entries = ref<SubstitutionEntry[]>([])
  const representing = ref<SubstitutionEntry[]>([])
  const candidates = ref<SubstitutionCandidate[]>([])
  const drafts = ref<DraftRow[]>([])
  const savedSnapshot = ref('')
  const formError = ref('')
  // Abwesenheitsnotiz (Kundenfeedback 18.10.2026, V)
  const notice = ref<AbsenceNotice>({ enabled: false, message: '' })
  const defaultNoticeText = ref('')
  const ownerName = computed(() => entries.value[0]?.userName || '')

  let nextKey = 1

  const todayISO = new Date().toISOString().slice(0, 10)

  const form = reactive({
    idSubstitute: 0,
    validFrom: todayISO,
    validUntil: '',
    note: '',
    caseScope: 'all' as SubstitutionCaseScope,
    caseScopeRule: '',
  })

  const scopeOptions = SUBSTITUTION_CASE_SCOPES

  const activeEntries = computed(() => entries.value.filter(e => e.isActive))
  const activeRepresenting = computed(() => representing.value.filter(e => e.isActive))

  const availableCandidates = computed(() =>
    candidates.value.filter(c => !drafts.value.some(d => d.idSubstitute === c.id)),
  )

  const canAdd = computed(() =>
    form.idSubstitute > 0
    && !!form.validFrom
    && (form.caseScope !== 'custom' || isValidSubstitutionScopeRule(form.caseScopeRule)),
  )

  const snapshot = () => JSON.stringify({
    drafts: drafts.value.map(d => toDraft(d)),
    notice: { enabled: notice.value.enabled, message: notice.value.message },
  })
  const dirty = computed(() => snapshot() !== savedSnapshot.value)

  watch(dirty, v => emit('dirty', v))

  function toDraft (d: DraftRow): SubstitutionDraft {
    return {
      idSubstitute: d.idSubstitute,
      validFrom: d.validFrom,
      validUntil: d.validUntil || null,
      note: d.note || '',
      caseScope: d.caseScope || 'all',
      caseScopeRule: d.caseScope === 'custom' ? (d.caseScopeRule || '').trim() : '',
    }
  }

  function scopeLabel (d: { caseScope?: string, caseScopeRule?: string }): string {
    return describeSubstitutionScope(d.caseScope, d.caseScopeRule)
  }

  function scopedSubstituteName (e: SubstitutionEntry): string {
    const scope = scopeLabel(e)
    return scope ? `${e.substituteName} (${scope})` : e.substituteName
  }

  function formatDate (iso: string | null): string {
    return formatSubstitutionDate(iso)
  }

  function candidateName (id: number): string {
    const c = candidates.value.find(x => x.id === id)
    if (c) return c.name
    const e = entries.value.find(x => x.idSubstitute === id)
    return e ? e.substituteName : `#${id}`
  }

  function representingLabel (e: SubstitutionEntry): string {
    const parts: string[] = []
    const scope = scopeLabel(e)
    if (scope) parts.push(scope)
    if (e.validUntil) parts.push(`${gt('bis')} ${formatDate(e.validUntil)}`)
    return parts.length ? `${e.userName} (${parts.join(', ')})` : e.userName
  }

  function isDraftActive (d: DraftRow): boolean {
    if (!d.validFrom || d.validFrom > todayISO) return false
    return !d.validUntil || d.validUntil >= todayISO
  }

  function applyOverview (overview: { entries: SubstitutionEntry[], representing: SubstitutionEntry[], absenceNotice?: AbsenceNotice }) {
    entries.value = overview.entries || []
    representing.value = overview.representing || []
    defaultNoticeText.value = overview.absenceNotice?.defaultMessage || ''
    notice.value = {
      enabled: !!overview.absenceNotice?.enabled,
      message: overview.absenceNotice?.message || defaultNoticeText.value,
    }
    drafts.value = entries.value.map(e => ({
      key: nextKey++,
      idSubstitute: e.idSubstitute,
      validFrom: e.validFrom,
      validUntil: e.validUntil,
      note: e.note || '',
      caseScope: e.caseScope || 'all',
      caseScopeRule: e.caseScopeRule || '',
    }))
    savedSnapshot.value = snapshot()
  }

  async function load () {
    loading.value = true
    try {
      const [overview, cands] = await Promise.all([
        getSubstitutions(props.userId ?? null),
        getSubstitutionCandidates(props.userId ?? null),
      ])
      candidates.value = cands || []
      applyOverview(overview)
    } catch (error: unknown) {
      useSnackbarStore().showSnackbar({ message: (error as Error).message, type: 'error' })
    } finally {
      loading.value = false
    }
  }

  function addDraft () {
    formError.value = ''
    if (!canAdd.value) return
    if (form.validUntil && form.validUntil < form.validFrom) {
      formError.value = gt('Das Bis-Datum darf nicht vor dem Von-Datum liegen.')
      return
    }
    if (form.caseScope === 'custom' && !isValidSubstitutionScopeRule(form.caseScopeRule)) {
      formError.value = gt('Bitte gültige Nummernbereiche angeben, z. B. „1-50, 77, 120-“.')
      return
    }
    drafts.value = [
      ...drafts.value,
      {
        key: nextKey++,
        idSubstitute: form.idSubstitute,
        validFrom: form.validFrom,
        validUntil: form.validUntil || null,
        note: form.note.trim(),
        caseScope: form.caseScope,
        caseScopeRule: form.caseScope === 'custom' ? form.caseScopeRule.trim() : '',
      },
    ]
    form.idSubstitute = 0
    form.validUntil = ''
    form.note = ''
    form.caseScope = 'all'
    form.caseScopeRule = ''
  }

  function removeDraft (index: number) {
    drafts.value = drafts.value.filter((_, i) => i !== index)
  }

  async function save () {
    saving.value = true
    formError.value = ''
    try {
      if (notice.value.enabled && !notice.value.message.trim()) {
        formError.value = gt('Bitte einen Text für die Abwesenheitsnotiz eingeben.')
        return
      }
      const overview = await saveSubstitutions(drafts.value.map(d => toDraft(d)), props.userId ?? null, notice.value)
      applyOverview(overview)
      useSnackbarStore().showSnackbar({ message: gt('Vertretung gespeichert.'), type: 'success' })
      emit('saved')
    } catch (error: unknown) {
      formError.value = (error as Error).message
    } finally {
      saving.value = false
    }
  }

  onMounted(load)

  defineExpose({ reload: load, dirty })
</script>

<style scoped>
.sub-editor {
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.sub-editor__loading {
  display: flex;
  justify-content: center;
  padding: 24px 0;
}

/* Hinweis-Banner */
.sub-banner {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 10px;
  font-size: 0.86rem;
  line-height: 1.4;
}
.sub-banner--away {
  background: #fff7ed;
  color: #9a3412;
  box-shadow: inset 0 0 0 1px #fed7aa;
}
.sub-banner--represent {
  background: #ecfdf5;
  color: #065f46;
  box-shadow: inset 0 0 0 1px #a7f3d0;
}

/* Regel-Liste */
.sub-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.sub-empty {
  font-size: 0.86rem;
  color: var(--grey-color, #5f5f5f);
  padding: 6px 2px;
}
.sub-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 10px;
  background: var(--grey-light-color, #f3f5f7);
  border-left: 3px solid transparent;
}
.sub-row--active {
  border-left-color: var(--primary-color, #0076e5);
  background: var(--primary-light-color, #deeefb);
}
.sub-row__main {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 4px 12px;
}
.sub-row__name {
  font-weight: 600;
  font-size: 0.9rem;
}
.sub-row__period {
  font-size: 0.82rem;
  color: var(--grey-color, #5f5f5f);
  font-variant-numeric: tabular-nums;
}
.sub-row__note {
  flex-basis: 100%;
  font-size: 0.8rem;
  color: var(--grey-color, #5f5f5f);
}
/* Fallauswahl (Kundenfeedback 13.09.26, I) */
.sub-row__scope {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 8px;
  background: #fff;
  box-shadow: inset 0 0 0 1px var(--primary-color, #0076e5);
  color: var(--primary-color, #0076e5);
  font-size: 0.74rem;
  font-weight: 600;
}
.sub-field--scope {
  min-width: 210px;
}
.sub-scope-hint {
  flex-basis: 100%;
  display: flex;
  align-items: flex-start;
  gap: 6px;
  margin: 2px 0 0;
  font-size: 0.78rem;
  line-height: 1.4;
  color: var(--grey-color, #5f5f5f);
}
.sub-scope-hint svg {
  flex-shrink: 0;
  margin-top: 2px;
}
.sub-chip {
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 2px 8px;
  border-radius: 8px;
  background: var(--primary-color, #0076e5);
  color: #fff;
}
.sub-row__remove {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: var(--grey-color, #5f5f5f);
  cursor: pointer;
  transition: background-color 0.14s ease, color 0.14s ease;
}
.sub-row__remove:hover {
  background: #fdecea;
  color: #d32f2f;
}

/* Hinzufügen */
.sub-add {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 10px;
  padding: 12px;
  border-radius: 10px;
  border: 1.5px dashed var(--border-grey-color, #dcdfe3);
}
.sub-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 150px;
}
.sub-field--grow {
  flex: 1 1 200px;
}
.sub-label {
  font-size: 0.74rem;
  font-weight: 600;
  color: var(--grey-color, #5f5f5f);
  text-transform: uppercase;
  letter-spacing: 0.03em;
}
.sub-input {
  height: 38px;
  padding: 0 10px;
  border: 1.5px solid var(--border-grey-color, #dcdfe3);
  border-radius: 8px;
  font-size: 0.88rem;
  font-family: inherit;
  color: var(--dark-color, #1f2937);
  background: #fff;
  outline: none;
  transition: border-color 0.15s ease;
}
.sub-input:focus {
  border-color: var(--primary-color, #0076e5);
}

/* Buttons */
.sub-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 38px;
  padding: 0 14px;
  border: none;
  border-radius: 8px;
  background: var(--primary-color, #0076e5);
  color: #fff;
  font-size: 0.86rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.14s ease, opacity 0.14s ease;
}
.sub-btn:hover:not(:disabled) {
  background: #005fb8;
}
.sub-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
.sub-btn--secondary {
  background: #fff;
  color: var(--primary-color, #0076e5);
  box-shadow: inset 0 0 0 1.5px var(--primary-color, #0076e5);
}
.sub-btn--secondary:hover:not(:disabled) {
  background: var(--primary-light-color, #deeefb);
}

.sub-error {
  margin: 0;
  font-size: 0.84rem;
  color: var(--error-color, #ff5252);
}
.sub-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
}
.sub-dirty {
  font-size: 0.8rem;
  color: var(--warning-color, #fb8c00);
}
</style>
